/**
 * Serviço de Cache para orçamentos e itens
 * 
 * Este serviço implementa um mecanismo de cache para os orçamentos e seus itens,
 * reduzindo o número de requisições ao servidor e melhorando a performance.
 */

class CacheService {
  constructor() {
    // Cache para orçamentos completos
    this.orcamentosCache = null;
    
    // Cache para itens de um orçamento específico (id do orçamento => itens)
    this.itensCache = new Map();
    
    // Tempo máximo de vida do cache (10 minutos)
    this.cacheLifetime = 10 * 60 * 1000; // 10 minutos em ms
    
    // Timestamp da última atualização do cache de orçamentos
    this.lastOrcamentosCacheUpdate = null;
    
    // Map de timestamps para cache de itens (id do orçamento => timestamp)
    this.lastItensCacheUpdate = new Map();
  }
  
  /**
   * Verifica se o cache ainda é válido baseado no timestamp de atualização
   * @param {number} timestamp - Timestamp da última atualização
   * @returns {boolean} - Verdadeiro se o cache for válido
   */
  isCacheValid(timestamp) {
    if (!timestamp) return false;
    
    const now = Date.now();
    return (now - timestamp) < this.cacheLifetime;
  }
  
  /**
   * Obtém os orçamentos do cache, se disponíveis e válidos
   * @returns {Array|null} - Array de orçamentos ou null se o cache não estiver disponível
   */
  getOrcamentos() {
    // Verificar se o cache existe e está válido
    if (this.orcamentosCache && this.isCacheValid(this.lastOrcamentosCacheUpdate)) {
      
      return this.orcamentosCache;
    }
    
    
    return null;
  }
  
  /**
   * Armazena orçamentos no cache
   * @param {Array} orcamentos - Array de orçamentos para cachear
   */
  setOrcamentos(orcamentos) {
    if (!orcamentos) return;
    
    
    this.orcamentosCache = orcamentos;
    this.lastOrcamentosCacheUpdate = Date.now();
  }
  
  /**
   * Obtém os itens de um orçamento do cache, se disponíveis e válidos
   * @param {string} orcamentoId - ID do orçamento
   * @returns {Array|null} - Array de itens ou null se o cache não estiver disponível
   */
  getItens(orcamentoId) {
    if (!orcamentoId) return null;
    
    // Verificar se o cache de itens para este orçamento existe e está válido
    if (this.itensCache.has(orcamentoId) && 
        this.isCacheValid(this.lastItensCacheUpdate.get(orcamentoId))) {
      
      return this.itensCache.get(orcamentoId);
    }
    
    
    return null;
  }
  
  /**
   * Armazena itens de um orçamento no cache
   * @param {string} orcamentoId - ID do orçamento
   * @param {Array} itens - Array de itens para cachear
   */
  setItens(orcamentoId, itens) {
    if (!orcamentoId || !itens) return;
    
    
    this.itensCache.set(orcamentoId, itens);
    this.lastItensCacheUpdate.set(orcamentoId, Date.now());
  }
  
  /**
   * Invalida o cache de orçamentos
   */
  invalidateOrcamentos() {
    
    this.orcamentosCache = null;
    this.lastOrcamentosCacheUpdate = null;
  }
  
  /**
   * Invalida o cache de itens para um orçamento específico
   * @param {string} orcamentoId - ID do orçamento
   */
  invalidateItens(orcamentoId) {
    if (!orcamentoId) return;
    
    
    this.itensCache.delete(orcamentoId);
    this.lastItensCacheUpdate.delete(orcamentoId);
  }
  
  /**
   * Invalida todo o cache
   */
  invalidateAll() {
    
    this.invalidateOrcamentos();
    this.itensCache.clear();
    this.lastItensCacheUpdate.clear();
  }
  
  /**
   * Atualiza um orçamento no cache
   * @param {Object} orcamento - Orçamento atualizado
   */
  updateOrcamento(orcamento) {
    if (!orcamento || !orcamento.id) return;
    
    if (this.orcamentosCache && Array.isArray(this.orcamentosCache)) {
      
      
      // Encontrar o índice do orçamento no cache
      const index = this.orcamentosCache.findIndex(o => o.id === orcamento.id);
      
      if (index !== -1) {
        // Substituir o orçamento no cache
        this.orcamentosCache[index] = orcamento;
        this.lastOrcamentosCacheUpdate = Date.now();
      } else {
        // Se o orçamento não existir no cache, adicionar
        this.orcamentosCache.push(orcamento);
        this.lastOrcamentosCacheUpdate = Date.now();
      }
    }
  }
  
  /**
   * Remove um orçamento do cache
   * @param {string} orcamentoId - ID do orçamento a ser removido
   */
  removeOrcamento(orcamentoId) {
    if (!orcamentoId) return;
    
    if (this.orcamentosCache && Array.isArray(this.orcamentosCache)) {
      
      
      // Filtrar o orçamento do cache
      this.orcamentosCache = this.orcamentosCache.filter(o => o.id !== orcamentoId);
      this.lastOrcamentosCacheUpdate = Date.now();
      
      // Também remover os itens deste orçamento
      this.invalidateItens(orcamentoId);
    }
  }
  
  /**
   * Adiciona um novo orçamento ao cache
   * @param {Object} orcamento - Novo orçamento
   */
  addOrcamento(orcamento) {
    if (!orcamento) return;
    
    if (!this.orcamentosCache) {
      this.orcamentosCache = [orcamento];
      this.lastOrcamentosCacheUpdate = Date.now();
    } else if (Array.isArray(this.orcamentosCache)) {
      
      this.orcamentosCache.push(orcamento);
      this.lastOrcamentosCacheUpdate = Date.now();
    }
  }
}

// Exportar uma instância única do serviço
const cacheService = new CacheService();
export default cacheService; 